// Cloudflare Turnstile, guarding the sending of a sign-in code.
//
// Promise:
// - Only with a site key (GET /api/auth-config). Without one no script is
//   loaded and `getToken` answers undefined.
// - Invisible until needed: the widget runs on `execute` and shows a check in
//   its container only when Cloudflare asks for one.
// - A token is good once: every send resets and executes again.
// - `getToken` always ends: when no callback comes (script blocked or dead) a
//   clock rejects it, so the sending button comes back. While a person looks at
//   the check, the clock is longer.
import { useCallback, useRef } from "react";

const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const TOKEN_TIMEOUT_MS = 30_000;
const INTERACTIVE_TIMEOUT_MS = 180_000;

interface TurnstileApi {
  render(
    container: HTMLElement,
    options: {
      sitekey: string;
      execution: "execute";
      appearance: "interaction-only";
      theme: "light" | "dark";
      callback(token: string): void;
      "error-callback"(): void;
      "expired-callback"(): void;
      "timeout-callback"(): void;
      "before-interactive-callback"(): void;
      "unsupported-callback"(): void;
    },
  ): string;
  execute(id: string): void;
  reset(id: string): void;
  remove(id: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let script: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  script ??= new Promise<TurnstileApi>((resolve, reject) => {
    const el = document.createElement("script");
    el.src = SCRIPT_SRC;
    el.async = true;
    el.onload = () => {
      if (window.turnstile) return resolve(window.turnstile);
      script = null;
      reject(new Error("turnstile: loaded without window.turnstile"));
    };
    el.onerror = () => {
      // Maybe the network was down: the next send loads it again.
      script = null;
      reject(new Error("turnstile: script failed to load"));
    };
    document.head.append(el);
  });
  return script;
}

interface Widget {
  api: TurnstileApi;
  id: string;
}

export interface Turnstile {
  /** A callback ref: the container comes and goes with the page's steps. */
  containerRef(node: HTMLDivElement | null): (() => void) | undefined;
  getToken(): Promise<string | undefined>;
}

export function useTurnstile(siteKey: string | null, dark: boolean): Turnstile {
  const pending = useRef<{
    resolve(token: string): void;
    reject(error: Error): void;
  } | null>(null);
  const widget = useRef<Widget | null>(null);
  const ready = useRef<Promise<Widget> | null>(null);
  const container = useRef<HTMLDivElement | null>(null);
  const clock = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopClock = useCallback(() => {
    if (clock.current !== null) clearTimeout(clock.current);
    clock.current = null;
  }, []);

  const settle = useCallback(
    (token: string | null) => {
      stopClock();
      const waiting = pending.current;
      pending.current = null;
      if (!waiting) return;
      if (token === null) waiting.reject(new Error("turnstile: no token"));
      else waiting.resolve(token);
    },
    [stopClock],
  );

  const startClock = useCallback(
    (ms: number) => {
      stopClock();
      clock.current = setTimeout(() => settle(null), ms);
    },
    [stopClock, settle],
  );

  const mount = useCallback(
    (node: HTMLDivElement, key: string): Promise<Widget> => {
      const mine: Promise<Widget> = loadTurnstile().then((api) => {
        if (ready.current !== mine)
          throw new Error("turnstile: container is gone");
        const id = api.render(node, {
          sitekey: key,
          execution: "execute",
          appearance: "interaction-only",
          theme: dark ? "dark" : "light",
          callback: (token) => settle(token),
          "error-callback": () => settle(null),
          "expired-callback": () => settle(null),
          "timeout-callback": () => settle(null),
          "unsupported-callback": () => settle(null),
          "before-interactive-callback": () => {
            if (pending.current) startClock(INTERACTIVE_TIMEOUT_MS);
          },
        });
        widget.current = { api, id };
        return widget.current;
      });
      mine.catch(() => {});
      ready.current = mine;
      return mine;
    },
    [dark, settle, startClock],
  );

  const containerRef = useCallback(
    (node: HTMLDivElement | null) => {
      const detach = () => {
        container.current = null;
        ready.current = null;
        const current = widget.current;
        widget.current = null;
        if (current) current.api.remove(current.id);
        settle(null);
      };
      if (!node) {
        detach();
        return undefined;
      }
      if (!siteKey) return undefined;
      container.current = node;
      void mount(node, siteKey);
      return detach;
    },
    [siteKey, settle, mount],
  );

  const getToken = useCallback(async (): Promise<string | undefined> => {
    if (!siteKey) return undefined;
    const waiting = ready.current;
    if (!waiting) throw new Error("turnstile: no container yet");
    let made: Widget;
    try {
      made = await waiting;
    } catch (error) {
      // Never keep a failed load: try again in place.
      const node = container.current;
      if (!node || ready.current !== waiting) throw error;
      ready.current = null;
      made = await mount(node, siteKey);
    }
    return new Promise<string>((resolve, reject) => {
      settle(null);
      pending.current = { resolve, reject };
      startClock(TOKEN_TIMEOUT_MS);
      made.api.reset(made.id);
      made.api.execute(made.id);
    });
  }, [siteKey, settle, startClock, mount]);

  return { containerRef, getToken };
}
